import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { createContext } from 'react';

import { calcPositionMarginExcludingFee } from '@flipster/features/src/shared/calculator/adjust-margin';
import { getEffectiveIsolatedMax, getModeMaxLeverage } from '@flipster/features/src/shared/leverage/isolated-cap';
import { shouldShowCrossNudge } from '@flipster/features/src/shared/leverage/show-cross-nudge';
import { positionBracketBunja } from '@flipster/features/src/shared/position-bracket/state';
import { snapshotSpecBunja } from '@flipster/features/src/shared/snapshot/state';
import { SymbolScope } from '@flipster/features/src/shared/symbol/bunja';
import { tradeModeBunja } from '@flipster/features/src/trade-mode/state';
import { ModalDismissSignal } from '@flipster/features/src/utils/modal-error-boundary';
import { i18nBunja } from '@flipster/i18n';
import type { OpenPosition } from '@flipster/r230522/normal-sub/tables/private/positions';
import { marketsBunja } from '@flipster/r230522/original/market';
import { ordersBunja } from '@flipster/r230522/original/order';
import type { OpenPosition as LegacyOpenPosition } from '@flipster/r230522/original/position';
import { openPositionsBunja, positionsBunja } from '@flipster/r230522/original/position';
import { pswapsBunja } from '@flipster/r230522/original/pswaps';
import { V2Position } from '@flipster/socket/r230522/position';
import {
  applyBracketMultiplier,
  findBracketTierByLeverage,
  validateBracketLeverage,
} from '@flipster/utils/calculator/bracket-formula';
import Num from '@flipster/utils/num';
import type { OrderMarginType } from '@flipster/utils/types/base';

import { marginTypeFamilyBunja } from '@web/app/[locale]/(web)/trade/_misc/order-form/perpetual/bunja/base';

import { createClampedLeverageAtom } from './clamped-leverage-atom';

export const AdjustLeverageModalContext = createContext<V2Position | OpenPosition | null>(null);
export const AdjustLeverageScope = createScopeFromContext(AdjustLeverageModalContext);

/** position 없이 모달을 열 때, 기본 leverage를 전달하기 위한 context */
export const AdjustLeverageDefaultsContext = createContext<{ defaultLeverage: number } | null>(null);
export const AdjustLeverageDefaultsScope = createScopeFromContext(AdjustLeverageDefaultsContext);

export const adjustLeverageModalBunja = bunja(() => {
  const initPosition = bunja.use(AdjustLeverageScope);
  const defaults = bunja.use(AdjustLeverageDefaultsScope);
  const symbol = bunja.use(SymbolScope);
  const { pendingPositionsAtom } = bunja.use(ordersBunja);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { v2PositionsAtom } = bunja.use(positionsBunja);
  const { pswapsAtom } = bunja.use(pswapsBunja);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);
  const { positionBracketAtom, positionBracketMultiplierAtom, symbolLevelBracketParamsAtom } =
    bunja.use(positionBracketBunja);
  const { getT } = bunja.use(i18nBunja);
  const { tradeModeAtom } = bunja.use(tradeModeBunja);
  const { marketsAtom } = bunja.use(marketsBunja);
  const { marginTypeFamily } = bunja.use(marginTypeFamilyBunja);

  const t = getT();

  const instrumentAtom = atom((get) => {
    const instrumentsKeyBySymbol = get(instrumentsKeyBySymbolAtom);
    const instrument = instrumentsKeyBySymbol?.[symbol];
    if (!instrument) throw new Error(`Instrument not found: ${symbol}`);
    return instrument;
  });

  const pricePrecisionAtom = atom((get) => get(instrumentAtom).pricePrecision);
  /** Cross / bracket Tier‑1 max (not mode-aware). */
  const crossMaxAtom = atom((get) => get(instrumentAtom).maxLeverageHardLimit);
  const effectiveIsolatedMaxAtom = atom((get) =>
    getEffectiveIsolatedMax(get(crossMaxAtom), get(instrumentAtom).maxInitLeverageIsolatedMargin),
  );
  const quoteCurrencyAtom = atom((get) => get(instrumentAtom).quoteCurrency);

  const marginTypeAtom = atom((get): OrderMarginType => {
    if (initPosition) {
      const open = get(openPositionsAtom)?.[symbol]?.find((p) => p.slot === initPosition.slot);
      return open?.marginType ?? initPosition.marginType;
    }
    return get(marginTypeFamily(symbol));
  });

  /** Slider / input ceiling for the current margin mode. */
  const modeMaxLeverageAtom = atom((get) =>
    getModeMaxLeverage(get(marginTypeAtom), get(crossMaxAtom), get(effectiveIsolatedMaxAtom)),
  );

  // 입력 leverage는 항상 min(input, modeMax)로 clamp — max가 내려가면 값도 따라 내려간다.
  // 로직은 createClampedLeverageAtom(dep-free)에 있어 단독 unit test된다(clamped-leverage-atom.test.ts).
  const leverageAtom = createClampedLeverageAtom(
    initPosition?.initLeverage ?? defaults?.defaultLeverage ?? 1,
    modeMaxLeverageAtom,
  );

  /** Bracket-tier upper limit at the slider leverage (no margin input). */
  const maxPositionAtLeverageAtom = atom((get) => {
    const leverage = get(leverageAtom);
    const positionBracket = get(positionBracketAtom);
    const multiplier = get(positionBracketMultiplierAtom);
    if (!positionBracket) return null;

    const bracket = applyBracketMultiplier(positionBracket, multiplier);
    const tier = findBracketTierByLeverage(leverage, bracket);
    return tier?.notionalPositionSizeUpperLimit ?? null;
  });

  const wsTargetPositionAtom = atom((get) => {
    const openPositions = get(openPositionsAtom)?.[symbol] ?? [];
    return openPositions.find((p) => !Num(p.position).isZero()) ?? null;
  });

  const hasOpenPositionAtom = atom((get) => get(wsTargetPositionAtom) != null);

  const showCrossNudgeAtom = atom((get) =>
    shouldShowCrossNudge({
      marginType: get(marginTypeAtom),
      leverage: get(leverageAtom),
      crossMax: get(crossMaxAtom),
      effectiveIsolatedMax: get(effectiveIsolatedMaxAtom),
      hasOpenPosition: get(hasOpenPositionAtom),
    }),
  );

  const liqPriceAtLeverageAtom = atom((get) => {
    const position = get(wsTargetPositionAtom);
    return position?.liquidationPrice?.toString() ?? null;
  });

  const currentPendingPositionsAtom = atom((get) => {
    return get(pendingPositionsAtom)?.[symbol]?.filter((p) => p.slot === 0) ?? [];
  });

  const hasAnyPositionAtom = atom((get) => {
    return Boolean(initPosition) || get(currentPendingPositionsAtom).length > 0;
  });

  // --- position context 없이 열린 경우 (orderform의 form mode: hedge, oneway+pending-only, oneway+빈 상태) ---
  if (!initPosition) {
    if (!defaults) throw new Error('AdjustLeverageDefaultsContext is required when position is not provided');

    const currentPositionAtom = atom(() => null);

    // Phase 1: oneway에서 pending order의 leverage 미만으로 낮출 수 없다.
    // oneway slot 0의 pending order들은 모두 같은 leverage로 잠겨있으므로 첫 항목만 본다.
    const errorAtom = atom<{ message: string; action?: { label: string; onClick: () => void } } | null>((get) => {
      if (get(tradeModeAtom) !== 'OneWay') return null;

      const pendingPositions = get(currentPendingPositionsAtom);
      if (pendingPositions.length === 0) return null;

      const minLeverage = pendingPositions[0].initLeverage;
      const newLeverage = get(leverageAtom);
      if (newLeverage < minLeverage) {
        return {
          message: t('Position_leverage.r250401.Decrease_leverage_not_allowed_error'),
        };
      }
      return null;
    });

    const isDirtyAtom = atom((get) => get(leverageAtom) !== defaults.defaultLeverage);
    const isValidAtom = atom((get) => {
      const isDirty = get(isDirtyAtom);
      const error = get(errorAtom);
      if (!isDirty) return false;
      if (error) return false;
      return true;
    });

    return {
      symbol,
      leverageAtom,
      currentPositionAtom,
      currentPendingPositionsAtom,
      hasAnyPositionAtom,
      errorAtom,
      isValidAtom,
      isDirtyAtom,
      pricePrecisionAtom,
      crossMaxAtom,
      modeMaxLeverageAtom,
      showCrossNudgeAtom,
      tradeModeAtom,
      maxPositionAtLeverageAtom,
      liqPriceAtLeverageAtom,
      quoteCurrencyAtom,
      wsTargetPositionAtom,
      positionBracketAtom,
    };
  }

  // --- 구체 position을 추적하는 경우 (open-positions 테이블 또는 orderform의 real position) ---
  const currentPositionAtom = atom((get) => {
    const openPositions = get(openPositionsAtom);
    const openPosition = openPositions?.[initPosition.symbol]?.find((p) => p.slot === initPosition.slot);
    if (openPosition) return openPosition as V2Position;

    const positions = get(v2PositionsAtom);
    const position = positions?.find((p) => p.symbol === initPosition.symbol && p.slot === initPosition.slot);
    // openPositionsAtom은 v2PositionsAtom에서 position !== '0' 필터링된 결과이므로,
    // v2에 남아있더라도 position === '0' 이면 닫힌 포지션으로 간주하고 dismiss
    if (!position || position.position === '0') {
      throw new ModalDismissSignal(`Position not found: ${initPosition.symbol}/${initPosition.slot}`);
    }
    return position;
  });

  const isDirtyAtom = atom((get) => {
    const currentPosition = get(currentPositionAtom);
    return currentPosition.initLeverage !== get(leverageAtom);
  });

  const bracketValidationAtom = atom((get) => {
    const currentPosition = get(currentPositionAtom);
    const newLeverage = get(leverageAtom);
    const pswap = get(pswapsAtom)?.[symbol];
    const market = get(marketsAtom)?.[symbol];
    const tradeMode = get(tradeModeAtom);
    const positionBracket = get(positionBracketAtom);
    const pendingPositions = get(pendingPositionsAtom)?.[symbol] || [];
    const instrument = get(instrumentAtom);

    // adjust leverage bracket validation은 'Cross margin'에서만 진행한다.
    if (currentPosition.marginType !== 'Cross') {
      return { isValid: true, maxAvailableLeverage: 0 };
    }

    if (!pswap || !positionBracket || !instrument || !tradeMode) {
      return { isValid: false, maxAvailableLeverage: 0 };
    }

    const markPrice = Num(market?.markPrice || 0);
    const { existingPositionSize, existingMargin } = get(symbolLevelBracketParamsAtom);

    const currentPendingPositionSize = pendingPositions.reduce((acc, cur) => {
      if (cur.slot === currentPosition.slot) return acc.plus(Num(cur.leavesQty).multipliedBy(cur?.price || 0));
      return acc;
    }, Num(0));
    const currentPositionSize = Num(currentPosition.position)
      .abs()
      .multipliedBy(markPrice)
      .plus(currentPendingPositionSize || 0);
    const currentMargin = Num(calcPositionMarginExcludingFee(currentPosition as unknown as LegacyOpenPosition));

    const { isValid, maxAvailableLeverage } = validateBracketLeverage({
      tradeMode,
      symbolLevelParams: {
        existingPositionSize,
        newPositionSize: Num(0),
        existingMargin,
        newLeverage,
      },
      positionLevelParams: {
        currentPositionSize,
        newPositionSize: Num(0),
        currentMargin,
        newLeverage,
      },
      positionBracket,
      positionBracketMultiplier: get(positionBracketMultiplierAtom),
      options: { minLeverage: currentPosition.initLeverage },
    });

    return { isValid, maxAvailableLeverage };
  });

  const errorAtom = atom((get) => {
    const currentPosition = get(currentPositionAtom);
    const newLeverage = get(leverageAtom);

    if (newLeverage < currentPosition.initLeverage) {
      return {
        message: t('Position_leverage.r250401.Decrease_leverage_not_allowed_error'),
      };
    }

    const { isValid, maxAvailableLeverage } = get(bracketValidationAtom);
    if (!isValid) {
      return {
        message: t('Trade.r251219.Maximum_leverage_higher_adjust_position', {
          leverage: `${maxAvailableLeverage}x`,
        }),
        action: {
          label: t('Trade.r251215.View_leverage_margin_tiers'),
          onClick: () => {
            window.open(`/support/trading-rules/leverage-margin/${currentPosition.symbol}`, '_blank');
          },
        },
      };
    }

    return null;
  });

  const isValidAtom = atom((get) => {
    const isDirty = get(isDirtyAtom);
    const error = get(errorAtom);
    if (!isDirty) return false;
    if (error) return false;
    return true;
  });

  return {
    symbol,
    leverageAtom,
    currentPositionAtom,
    currentPendingPositionsAtom,
    hasAnyPositionAtom,
    errorAtom,
    isValidAtom,
    isDirtyAtom,
    pricePrecisionAtom,
    crossMaxAtom,
    modeMaxLeverageAtom,
    showCrossNudgeAtom,
    tradeModeAtom,
    maxPositionAtLeverageAtom,
    liqPriceAtLeverageAtom,
    quoteCurrencyAtom,
    wsTargetPositionAtom,
    positionBracketAtom,
  };
});
adjustLeverageModalBunja.debugLabel = 'adjustLeverageModalBunja';
